import { Seo } from '@wwtr/components/layout';
import { useRouter } from 'next/router';
import React, { PropsWithChildren } from 'react';
import { useQuery } from '@apollo/client';
import {
  Facilities_Facility_Entry,
  OrganizationDetailQuery,
  OrganizationLocations_OrganizationLocation_Entry,
  Organizations_Organization_Entry,
} from '@wwtr/generated';
import {
  BreadcrumbsItem,
  Button,
  Carousel,
  Container,
  Heading,
  Link,
  PageHeader,
  RichText,
  Section,
  Skeleton,
  Tile,
} from '@wwtr/components/ui';
import ORGANIZATION_DETAIL_QUERY from '@wwtr/gql/queries/organization-detail.gql';
import { getNetworkStatusDetails } from '@wwtr/libs/apollo/utils';
import { HeartIcon } from '@heroicons/react/outline';
import NextLink from 'next/link';
import { getFirstParagraph } from '@wwtr/libs/utils';
import { ExternalLink } from 'react-feather';
import { ArrowRightIcon } from '@heroicons/react/solid';
import { FAVORITE_TYPE, useFavorites } from '@wwtr/libs/contexts';

const LocationData = ({
  heading,
  children,
}: PropsWithChildren<{ heading: string }>) => (
  <>
    <dt className="text-sm text-gray-700">{heading}</dt>
    <dd className="mb-4">{children}</dd>
  </>
);

const OrganizationLocation = ({
  location,
}: {
  location: OrganizationLocations_OrganizationLocation_Entry;
}) => (
  <Tile
    padded
    size="md"
    variant="white"
    hasShadow
    className="flex flex-col sm:flex-row mb-6"
  >
    {location.visitAddress.lat && location.visitAddressHide === false && (
      <figure className="mb-4 sm:mb-auto sm:mr-4 md:mr-6 lg:mr-8 flex-shrink-0 overflow-hidden rounded-md">
        <img
          src={`https://maps.googleapis.com/maps/api/staticmap?center=${location.visitAddress.lat},${location.visitAddress.lng}&markers=color:0x2a378d|size:mid|${location.visitAddress.lat},${location.visitAddress.lng}&zoom=14&size=575x208&scale=2&maptype=roadmap&key=AIzaSyALAoCzT99hIXU-2VR94P8Bdhz2xp4IWXY`}
          title=""
          className="sm:w-52 h-52 object-cover"
        />
      </figure>
    )}
    <div className="col-span-3 space-y-4 sm:space-y-6">
      <Heading level={3}>{location.title}</Heading>
      <dl>
        {location.visitAddress.parts.address &&
          location.visitAddressHide === false && (
            <LocationData heading="Bezoekadres">
              <address className="mb-2">
                <p>
                  {location.visitAddress.parts.address}{' '}
                  {location.visitAddress.parts.number}
                </p>
                <p>
                  {location.visitAddress.parts.postcode}{' '}
                  {location.visitAddress.parts.city}
                </p>
              </address>
              <Button
                as="a"
                size="xs"
                color="powerblue"
                appearance="outline"
                target="_blank"
                withIcon
                href={`https://maps.google.com/maps?daddr=${encodeURIComponent(
                  location.visitAddress.parts.address.trim() +
                    ' ' +
                    location.visitAddress.parts.number.trim() +
                    ',' +
                    location.visitAddress.parts.city.trim()
                )}`}
                title={`Plan route naar ${location.title} met Google Maps`}
              >
                <span>Plan route met Google Maps</span>
                <ExternalLink className="inline ml-1 w-4 align-top" />
              </Button>
            </LocationData>
          )}
        {location.postalAddress.parts.address &&
          location.postalAddressHide === false && (
            <LocationData heading="Postadres">
              <address className="mb-2">
                <p>
                  {location.postalAddress.parts.address}{' '}
                  {location.postalAddress.parts.number}
                </p>
                <p>
                  {location.postalAddress.parts.postcode}{' '}
                  {location.postalAddress.parts.city}
                </p>
              </address>
            </LocationData>
          )}
        {location.phone && (
          <LocationData heading="Telefoonnummer">{location.phone}</LocationData>
        )}
        {location.email && (
          <LocationData heading="E-mailadres">
            <Link
              href={`mailto:${location.email}`}
              title="Stuur een e-mail naar dit adres"
            >
              {location.email}
            </Link>
          </LocationData>
        )}
        {location.website && (
          <LocationData heading="Website">
            <Link
              href={location.website}
              target="_blank"
              title="Open link in een nieuw venster"
            >
              <span>{new URL(location.website).host}</span>
              <ExternalLink className="inline ml-1 w-4 align-top" />
            </Link>
          </LocationData>
        )}
        <LocationData heading="Toegankelijk voor gehandicapten">
          {location.handicapAccessible}
        </LocationData>
        {location.accessibilityFeatures.length > 0 && (
          <LocationData heading="Toegankelijkheid voorzieningen">
            <ul>
              {location.accessibilityFeatures.map((feature, index) => (
                <li key={`location_accessibility_feature_${index}`}>
                  {feature}
                </li>
              ))}
            </ul>
          </LocationData>
        )}
      </dl>
    </div>
  </Tile>
);

const OrganizationFacility = ({
  facility,
}: {
  facility: Facilities_Facility_Entry;
}) => (
  <Tile
    padded
    size="md"
    variant="white"
    hasShadow
    hoverScale
    className="group flex h-full lg:h-auto flex-col mb-6"
  >
    <Heading level={3} className="mb-4">
      <NextLink href={`/${facility.uri}`}>
        <Link
          href={`/${facility.uri}`}
          noWrap={false}
          title={`Lees meer over ${facility.title}`}
        >
          {facility.title}
        </Link>
      </NextLink>
    </Heading>
    <p
      className="text-lg mb-4"
      dangerouslySetInnerHTML={{
        __html: getFirstParagraph(facility.description, 160),
      }}
    ></p>
    <div className="flex flex-row justify-end group">
      <NextLink href={`/${facility.uri}`} passHref>
        <Button
          as="a"
          color="primary"
          appearance="outline"
          withIcon
          size="sm"
          title={`Lees meer over ${facility.title}`}
        >
          <span>Lees meer</span>
          <ArrowRightIcon className="w-4" />
        </Button>
      </NextLink>
    </div>
  </Tile>
);

const OrganizationDetailPage = () => {
  const router = useRouter();
  const { organization } = router.query;

  const { toggleFavorite, favorites } = useFavorites();

  const onClickFavorite = ($event, id: string) => {
    toggleFavorite(FAVORITE_TYPE.ORGANIZATION, id);
  };

  const {
    data: organizationDetailData,
    networkStatus: organizationDetailNetworkStatus,
    error: organizationDetailError,
  } = useQuery<OrganizationDetailQuery>(ORGANIZATION_DETAIL_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      slug: organization,
    },
    skip: !organization,
  });

  const {
    isLoading: organizationDetailIsLoading,
    isReady: organizationDetailIsReady,
  } = getNetworkStatusDetails(organizationDetailNetworkStatus);

  // if (organizationDetailIsReady && (organizationDetailError || !organizationDetailData?.entry)) {
  //   return <App404 />;
  // }

  let breadCrumbs: BreadcrumbsItem[] = [
    {
      url: '/zoek/organisaties',
      label: 'Organisaties',
    },
  ];

  const entry =
    organizationDetailData?.entry as Organizations_Organization_Entry;

  const facilities =
    organizationDetailData?.facilities as Facilities_Facility_Entry[];

  const isFavorite = favorites?.organizations?.includes(entry?.id);

  if (organizationDetailIsReady) {
    // Always push own entry
    breadCrumbs.push({
      label: entry?.title,
      shorten: true,
    });
  }

  return (
    <article>
      <Seo title={entry?.title} />
      <PageHeader
        title={entry?.title}
        isLoading={organizationDetailIsLoading}
        breadCrumbs={breadCrumbs}
      />

      <Section>
        <Container>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-8 xl:col-span-7">
              {organizationDetailIsLoading ? (
                <div className="space-y-6 mt-4">
                  <Skeleton className="w-full h-5 lg:h-6"></Skeleton>
                  <Skeleton className="w-7/12 h-5 lg:h-6"></Skeleton>
                  <Skeleton className="w-9/12 h-5 lg:h-6"></Skeleton>
                  <Skeleton className="w-1/3 h-5 lg:h-6"></Skeleton>
                </div>
              ) : (
                <div className="mb-8 text-2xl leading-snug lg:text-3xl lg:leading-snug text-gray-700">
                  <RichText content={entry?.description} />
                </div>
              )}

              {!organizationDetailIsLoading && (
                <>
                  <div className="mb-16">
                    <Heading
                      variant="gray-800"
                      level={2}
                      className="mb-8"
                      isLoading={organizationDetailIsLoading}
                    >
                      Locaties
                    </Heading>
                    <div>
                      {entry?.locations.map(
                        (
                          location: OrganizationLocations_OrganizationLocation_Entry,
                          locationIndex
                        ) => (
                          <OrganizationLocation
                            location={location}
                            key={`location_${locationIndex}`}
                          />
                        )
                      )}
                    </div>
                  </div>

                  <div className="mb-16">
                    <Heading
                      variant="gray-800"
                      level={2}
                      className="mb-8"
                      isLoading={organizationDetailIsLoading}
                    >
                      Hulpaanbod
                    </Heading>
                    <div className="-mx-4 lg:mx-0">
                      <Carousel>
                        <ul className="grid grid-flow-col lg:grid-flow-row lg:grid-cols-1 gap-8 lg:gap-2">
                          {facilities?.map((facility, facilityIndex) => (
                            <li
                              key={`organization_facility_${facilityIndex}`}
                              className="w-80vw sm:w-60vw md:w-40vw lg:w-auto"
                            >
                              <OrganizationFacility
                                key={`facility_${facilityIndex}`}
                                facility={facility}
                              />
                            </li>
                          ))}
                        </ul>
                      </Carousel>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="hidden lg:block col-span-12 lg:col-span-4 xl:col-start-9 print:hidden">
              <div className="sticky top-32">
                <div className="mb-8">
                  <Button
                    appearance={isFavorite ? 'solid' : 'outline'}
                    color="hotpink"
                    fullWidth={true}
                    withIcon
                    className="mb-4"
                    onClick={$event => onClickFavorite($event, entry.id)}
                  >
                    <HeartIcon className="w-6" />
                    {isFavorite ? (
                      <span>Verwijder uit favorieten</span>
                    ) : (
                      <span>Opslaan in favorieten</span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <Section as="aside" className="bg-powerblue print:hidden">
        <Container>
          <Heading level={2} variant="white" className="mb-12">
            Niet gevonden wat u zocht?
          </Heading>
          <div className="inline-flex flex-wrap items-baseline">
            <p className="text-white opacity-80 mb-4 mr-4">Bekijk meer:</p>
            {entry?.organizationCategory.map((category, index) => (
              <div key={`category${index}`} className="mb-4 mr-4">
                <NextLink
                  href={`/zoek/organisaties/?c=${category.id}`}
                  passHref
                >
                  <Button
                    as={'a'}
                    appearance="outline"
                    color="white"
                    size="sm"
                    title={`Lees meer over ${category.title}`}
                  >
                    {category.title}
                  </Button>
                </NextLink>
              </div>
            ))}
          </div>
        </Container>
      </Section>
    </article>
  );
};

// export async function getStaticPaths() {
//   return {
//     paths: [
//       { params: { ... } } // See the "paths" section below
//     ],
//     fallback: true, false, or 'blocking' // See the "fallback" section below
//   };
// }

export default OrganizationDetailPage;
